import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useOnboardingState } from '@/contexts/OnboardingContext';
import { CAPS } from '@/lib/kbCaps';
import { extractKbFieldErrors, type FieldErrorMap } from '@/lib/kbFieldErrors';
import { FieldError } from '@/components/onboarding/enrichment/FieldError';
import { CharCounter } from '@/components/shared/CharCounter';

interface DifferentiatorsEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialValue: string | null;
  /** F075: "Quem conduz" — credentials of who delivers the service. */
  initialTeamCredentials: string | null;
}

export function DifferentiatorsEditor({
  open,
  onOpenChange,
  initialValue,
  initialTeamCredentials,
}: DifferentiatorsEditorProps) {
  const { t } = useTranslation();
  const { updateKbProfile } = useOnboardingState();
  const [value, setValue] = useState(initialValue ?? '');
  const [teamCredentials, setTeamCredentials] = useState(initialTeamCredentials ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrorMap | null>(null);

  async function handleSave() {
    setSaving(true);
    setError(null);
    setFieldErrors(null);
    try {
      await updateKbProfile({
        differentiators: value || null,
        team_credentials: teamCredentials || null,
      });
      onOpenChange(false);
    } catch (err) {
      const fe = extractKbFieldErrors(err);
      if (fe) {
        setFieldErrors(fe);
      } else {
        setError(t('common.error_generic'));
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('bot.enrich.differentiators.title')}</DialogTitle>
          <DialogDescription>{t('bot.enrich.differentiators.description')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Label htmlFor="differentiators-text">
            {t('bot.enrich.differentiators.field_label')}
          </Label>
          <Textarea
            id="differentiators-text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={t('bot.enrich.differentiators.placeholder')}
            maxLength={CAPS.differentiators}
            rows={5}
          />
          <CharCounter value={value} max={CAPS.differentiators} />
          <FieldError errors={fieldErrors} path="differentiators" />
          <Label htmlFor="team-credentials-text">
            {t('bot.enrich.differentiators.team_credentials_label')}
          </Label>
          <p className="text-xs text-muted-foreground">
            {t('bot.enrich.differentiators.team_credentials_hint')}
          </p>
          <Textarea
            id="team-credentials-text"
            value={teamCredentials}
            onChange={(e) => setTeamCredentials(e.target.value)}
            placeholder={t('bot.enrich.differentiators.team_credentials_placeholder')}
            maxLength={CAPS.team_credentials}
            rows={4}
          />
          <CharCounter value={teamCredentials} max={CAPS.team_credentials} />
          <FieldError errors={fieldErrors} path="team_credentials" />
          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="min-h-11"
          >
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving} className="min-h-11">
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
